/**
 * The route tables (TRD §4 "Frontend").
 *
 * Two routers, picked by main.tsx from the address. Inside a project
 * (`/p/<slug>/...`) the project router runs with `basename: "/p/<slug>"`, so
 * every page and link below is written project-relative (`/experiments/1`).
 * Everywhere else the root router serves `/login`, opens the last project
 * used at `/` (or the first-project screen for someone in none), and sends old
 * unprefixed links to the last project used. There is no projects page: the
 * sidebar's switcher lists and switches them.
 *
 * In both, the split is the security boundary: `/login` sits outside
 * `RequireAuth`, everything else sits inside it. Adding a page means adding a
 * child of the guarded branch, so a new screen is protected by default rather
 * than by remembering to protect it. `ProjectGate` loads the project and stops
 * at "not found or no access" before any project page renders.
 *
 * Panels and filters are URL state too: `/experiments/new?agent=` starts the
 * new-experiment flow from an agent, `?item=` opens an item, `?add=1` / `?new=1`
 * open the create dialogs, `/settings?tab=credentials` picks a settings tab
 * (the old `/settings/<tab>` links redirect there). `/compare?ids=a,b,c` keeps
 * the whole comparison (reference, tab, axes, filters) in its query string.
 * `/annotation?tab=scores` picks the Annotate tab; `/annotation/:id` is one queue.
 * `/users` (workbench admins) keeps search, status and page in the query string and
 * opens one person with `?user=<id>`. It also works with no project: the root
 * router serves `/users`, and `/p/<slug>/users` falls back to it when the
 * project won't open.
 */

import { ProjectRoute } from "@/features/projects/project-route";
import { RouteError } from "@/components/route-error";
import { Navigate, createBrowserRouter, useParams, type RouteObject } from "react-router";
import { Layout } from "@/components/layout";
import { RequireAuth } from "@/components/require-auth";
import { EmptyState } from "@/components/ui/states";
import { ButtonLink } from "@/components/ui/button";
import { LoginPage } from "@/features/auth/login-page";
import { AgentPage } from "@/features/agents/agent-page";
import { AgentsPage } from "@/features/agents/agents-page";
import { CsvImportPage } from "@/features/datasets/csv-import-page";
import { DatasetPage } from "@/features/datasets/dataset-page";
import { DatasetsPage } from "@/features/datasets/datasets-page";
import { EvaluatorConfigurePage } from "@/features/evaluators/configure-page";
import { EvaluatorsPage } from "@/features/evaluators/evaluators-page";
import { ExperimentPage } from "@/features/experiments/experiment-page";
import { ExperimentsPage } from "@/features/experiments/experiments-page";
import { NewExperimentPage } from "@/features/experiments/new/new-experiment-page";
import { HomePage } from "@/features/home/home-page";
import { ComparePage } from "@/features/compare/compare-page";
import { SettingsPage, SettingsRedirect } from "@/features/settings/settings-page";
import { AnnotationQueuesPage } from "@/features/annotation/queues-page";
import { AnnotationQueuePage } from "@/features/annotation/queue-page";
import { LegacyRedirect } from "@/features/projects/legacy-redirect";
import { OpenLastProject } from "@/features/projects/open-last-project";
import { ProjectGate } from "@/features/projects/project-gate";
import { UsersPage } from "@/features/users/users-page";
import { UsersScreen } from "@/features/users/users-screen";

function NotFound() {
  return (
    <EmptyState title="Nothing here" action={<ButtonLink to="/">Go home</ButtonLink>}>
      This link does not match a page in the workbench.
    </EmptyState>
  );
}

/** `/agents/:id/new` was the old new-experiment panel; old links land on the flow. */
function OldNewExperimentLink() {
  const { id = "" } = useParams();
  return <Navigate to={`/experiments/new?agent=${encodeURIComponent(id)}`} replace />;
}

// createBrowserRouter (not HashRouter): the backend's SPA catch-all returns
// index.html for unknown paths, so real URLs work on refresh and when shared.

/** Outside any project: sign-in, `/` (opens your last project), and old links. */
export const createRootRouter = () =>
  createBrowserRouter([
    { path: "/login", element: <LoginPage /> },
    {
      element: <RequireAuth />,
      errorElement: <RouteError />,
      children: [
        { path: "/", element: <OpenLastProject /> },
        // Workbench admins manage people, not projects, so this needs no project.
        { path: "/users", element: <UsersScreen /> },
      ],
    },
    // The projects page that was here: old links and bookmarks open a project.
    { path: "/projects", element: <Navigate to="/" replace /> },
    { path: "*", element: <LegacyRedirect /> },
  ]);

const projectRoutes: RouteObject[] = [
  {
    element: <RequireAuth />,
    // A crash anywhere above the layout (the gate, the guard) still gets a page.
    errorElement: <RouteError />,
    children: [
      {
        element: <ProjectGate />,
        errorElement: <RouteError />,
        children: [
          {
            element: <ProjectRoute />,
            children: [
              {
                path: "/",
                element: <Layout />,
                errorElement: <RouteError />,
                children: [
                  { index: true, element: <HomePage /> },
                  { path: "agents", element: <AgentsPage /> },
                  { path: "agents/:id", element: <AgentPage /> },
                  { path: "agents/:id/new", element: <OldNewExperimentLink /> },
                  { path: "experiments", element: <ExperimentsPage /> },
                  { path: "experiments/new", element: <NewExperimentPage /> },
                  { path: "experiments/:id", element: <ExperimentPage /> },
                  { path: "compare", element: <ComparePage /> },
                  { path: "datasets", element: <DatasetsPage /> },
                  { path: "datasets/:id", element: <DatasetPage /> },
                  { path: "datasets/:id/import", element: <CsvImportPage /> },
                  { path: "evaluators", element: <EvaluatorsPage /> },
                  { path: "evaluators/new", element: <EvaluatorConfigurePage /> },
                  { path: "evaluators/:id", element: <EvaluatorConfigurePage /> },
                  { path: "annotation", element: <AnnotationQueuesPage /> },
                  { path: "annotation/:id", element: <AnnotationQueuePage /> },
                  { path: "settings", element: <SettingsPage /> },
                  { path: "users", element: <UsersPage /> },
                  { path: "settings/connections", element: <SettingsRedirect tab="connections" /> },
                  { path: "settings/credentials", element: <SettingsRedirect tab="credentials" /> },
                  { path: "*", element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

/** One project's pages, under `/p/<slug>`. */
export const createProjectRouter = (slug: string) =>
  createBrowserRouter(projectRoutes, { basename: `/p/${encodeURIComponent(slug)}` });
